import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Form,
  Badge, Alert, Spinner, InputGroup, Modal
} from 'react-bootstrap';
import {
  Plus, Calendar, Filter, Download, RefreshCw,
  Trash2, Search, X, TrendingDown, User, Tag, Lock, Unlock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import expenseService from '../../services/expenseService';
import { cashSessionService } from '../../services/Cashsessionservice';
import CloseSessionModal from '../cashSession/Closesessionmodal';

const ExpensesList = () => {
  const { user, isManager } = useAuth();
  const { currentBoutique, isRestaurantEnabled } = useBoutique();

  // États
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalExpenses, setTotalExpenses] = useState(0);

  // ✅ Session de caisse (module restaurant)
  const [currentSession, setCurrentSession] = useState(null);
  const [showCloseSessionModal, setShowCloseSessionModal] = useState(false);
  const restaurantSessionActiveRef = useRef(false);

  // Filtres
  const [filters, setFilters] = useState({
    period: 'week', // today, week, month, custom
    startDate: '',
    endDate: '',
    category: 'all',
    creator: 'all',
    searchQuery: ''
  });

  // Modal création dépense
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newExpense, setNewExpense] = useState({
    amount: '',
    description: '',
    category: 'GENERAL',
    date: new Date().toISOString().slice(0, 16)
  });

  // Modal export Excel
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDates, setExportDates] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10)
  });
  const [exporting, setExporting] = useState(false);

  // Chargement initial
  // Chargement initial
  useEffect(() => {
    loadExpenses();
  }, [currentBoutique, filters.period, filters.startDate, filters.endDate, currentSession?.id]);

  // ✅ Charger la session courante (module restaurant)
  useEffect(() => {
    const loadCurrentSession = async () => {
      if (!isRestaurantEnabled) { setCurrentSession(null); return; }
      try {
        const response = await cashSessionService.getCurrentSession();
        setCurrentSession(response.success && response.data ? response.data : null);
      } catch (e) {
        console.error('❌ Erreur session courante:', e);
        setCurrentSession(null);
      }
    };
    loadCurrentSession();
  }, [isRestaurantEnabled, currentBoutique]);

  useEffect(() => {
    restaurantSessionActiveRef.current = !!(
      isRestaurantEnabled && currentSession?.status === 'OPEN'
    );
  }, [isRestaurantEnabled, currentSession]);

  // Appliquer les filtres
  useEffect(() => {
    applyFilters();
  }, [expenses, filters.category, filters.creator, filters.searchQuery]);

  /**
   * Charger les dépenses selon la période
   */
  const loadExpenses = async () => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Mode session : uniquement les dépenses de la session active
      if (isRestaurantEnabled && currentSession?.status === 'OPEN') {
        const resp = await cashSessionService.getSessionExpenses(currentSession.id);
        const sessionData = resp.success && Array.isArray(resp.data) ? resp.data : [];
        setExpenses(sessionData);
        calculateTotal(sessionData);
        return;
      }

      let data = [];

      if (filters.period === 'today') {
        data = await expenseService.getExpensesByDate(new Date());
      }
      else if (filters.period === 'custom' && filters.startDate && filters.endDate) {
        const params = {
        startDate: `${filters.startDate}T00:00:00`,
        endDate: `${filters.endDate}T23:59:59`
      };
        data = await expenseService.getAllExpenses(params);
      }
      else {
        data = await expenseService.getAllExpenses();

        if (filters.period === 'week') {
          const { start, end } = getWeekRange();
          data = data.filter(expense => {
            const expenseDate = new Date(expense.date || expense.createdAt);
            return expenseDate >= start && expenseDate <= end;
          });
        }
        else if (filters.period === 'month') {
          const today = new Date();
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          startOfMonth.setHours(0, 0, 0, 0);
          const endOfMonth = new Date(today);
          endOfMonth.setHours(23, 59, 59, 999);
          data = data.filter(expense => {
          const expenseDate = new Date(expense.date || expense.createdAt);
          return expenseDate >= startOfMonth && expenseDate <= endOfMonth;
        });
      }
      else if (filters.period === 'year') {
        const today = new Date();
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        startOfYear.setHours(0, 0, 0, 0);

        const endOfYear = new Date(today);
        endOfYear.setHours(23, 59, 59, 999);

        data = data.filter(expense => {
          const expenseDate = new Date(expense.date || expense.createdAt);
          return expenseDate >= startOfYear && expenseDate <= endOfYear;
        });
      }
      // filters.period === 'all' → aucun filtrage, toutes les dépenses
    }

      // ✅ Session devenue active pendant la requête → ne pas écraser
      if (restaurantSessionActiveRef.current) return;

      setExpenses(data);
      calculateTotal(data);
    } catch (err) {
      console.error('❌ Erreur chargement dépenses:', err);
      setError('Impossible de charger les dépenses');
    } finally {
      setLoading(false);
    }
  };



  /**
   * Obtenir la plage de dates de la semaine en cours (incluant aujourd'hui)
   */
  const getWeekRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    
    // Début de la semaine (lundi)
    const start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    start.setHours(0, 0, 0, 0);

    // ✅ CORRECTION : Fin = aujourd'hui à 23h59
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);

    return { start, end };
  };

  /**
   * Calculer le total des dépenses
   */
  const calculateTotal = (data) => {
    const total = data.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
    setTotalExpenses(total);
  };

  /**
   * Appliquer les filtres locaux
   */
  const applyFilters = () => {
    let filtered = [...expenses];

    // Filtre par catégorie
    if (filters.category !== 'all') {
      filtered = filtered.filter(exp => exp.category === filters.category);
    }

    // Filtre par créateur (pour managers)
    if (isManager && filters.creator !== 'all') {
      filtered = filtered.filter(exp => exp.createdByName === filters.creator);
    }

    // Recherche textuelle
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(exp =>
        exp.description.toLowerCase().includes(query) ||
        exp.createdByName.toLowerCase().includes(query)
      );
    }

    setFilteredExpenses(filtered);
    calculateTotal(filtered);
  };

  /**
   * Créer une dépense
   */
  const handleCreateExpense = async () => {
    try {
      const validation = expenseService.validateExpense(newExpense);
      if (!validation.isValid) {
        alert(validation.errors.join('\n'));
        return;
      }

      const response = await expenseService.addExpense({
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        boutiqueId: currentBoutique?.id
      });

      if (response.success) {
        setShowCreateModal(false);
        resetNewExpense();
        loadExpenses();
      } else {
        alert(response.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création dépense:', err);
      alert('Erreur de connexion');
    }
  };

  const handleSessionClosed = (closedSession) => {
    setShowCloseSessionModal(false);
    setCurrentSession(null); // déclenche le rechargement en mode journalier
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      console.error('Date invalide:', dateString);
      return '';
    }
    
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Dakar',
      hour12: false
    });
  };

  /**
   * Supprimer une dépense
   */
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette dépense ?')) return;

    try {
      const response = await expenseService.deleteExpense(expenseId);
      if (response.success) {
        loadExpenses();
      } else {
        alert(response.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur de connexion');
    }
  };

  /**
   * Exporter les dépenses vers Excel
   */
  const handleExportToExcel = async () => {
    try {
      setExporting(true);

      // Valider les dates
      if (!exportDates.startDate || !exportDates.endDate) {
        alert('Veuillez sélectionner une plage de dates');
        return;
      }

      const start = new Date(exportDates.startDate);
      start.setHours(0, 0, 0, 0);
      
      const end = new Date(exportDates.endDate);
      end.setHours(23, 59, 59, 999);
      
      if (start > end) {
        alert('La date de début doit être antérieure à la date de fin');
        return;
      }

      // Charger TOUTES les dépenses (le backend filtre déjà selon le rôle)
      const allExpenses = await expenseService.getAllExpenses();
      

      // Filtrer les dépenses selon la plage de dates côté client
      const expensesToExport = allExpenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        const isInRange = expenseDate >= start && expenseDate <= end;
        
        if (isInRange) {
          
        }
        
        return isInRange;
      });


      if (expensesToExport.length === 0) {
        alert(`Aucune dépense trouvée entre le ${start.toLocaleDateString('fr-FR')} et le ${end.toLocaleDateString('fr-FR')}`);
        return;
      }

      // Exporter vers Excel
      const result = await expenseService.exportExpensesToExcel(
        expensesToExport,
        exportDates.startDate,
        exportDates.endDate,
        currentBoutique?.name
      );

      if (result.success) {
        alert(`✅ Export réussi : ${expensesToExport.length} dépense(s) exportée(s)`);
        setShowExportModal(false);
      } else {
        alert(result.message || 'Erreur lors de l\'export');
      }

    } catch (err) {
      console.error('❌ Erreur export:', err);
      alert('Erreur lors de l\'export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  /**
   * Réinitialiser le formulaire
   */
  const resetNewExpense = () => {
    setNewExpense({
      amount: '',
      description: '',
      category: 'GENERAL',
      date: new Date().toISOString().slice(0, 16)
    });
  };

  /**
   * Obtenir les créateurs uniques (pour filtre)
   */
  const getUniqueCreators = () => {
    const creators = new Set(expenses.map(exp => exp.createdByName));
    return Array.from(creators);
  };

  /**
   * Changer la période
   */
  const handlePeriodChange = (period) => {
    setFilters(prev => ({ ...prev, period, startDate: '', endDate: '' }));
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <TrendingDown className="me-2" size={28} />
                Gestion des Dépenses
              </h2>
              <p className="text-muted mb-0">
                {isManager ? 'Toutes les dépenses de la boutique' : 'Mes dépenses'}
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="success" onClick={() => setShowExportModal(true)}>
                <Download size={18} className="me-2" />
                Exporter Excel
              </Button>
              <Button variant="outline-primary" onClick={loadExpenses}>
                <RefreshCw size={18} className="me-2" />
                Actualiser
              </Button>
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={18} className="me-2" />
                Nouvelle Dépense
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* ✅ BANDEAU SESSION ACTIVE (module restaurant) */}
      {isRestaurantEnabled && currentSession && currentSession.status === 'OPEN' && (
        <Alert variant="success" className="d-flex justify-content-between align-items-center py-2 mb-3">
          <div>
            <Lock size={16} className="me-2" />
            <strong>Session {currentSession.session_number}</strong>
            <span className="d-none d-md-inline">
              {' '}— Fond : {cashSessionService.formatCurrency(currentSession.opening_amount)}
              {' '}— Durée : {cashSessionService.formatDuration(currentSession.duration_minutes)}
            </span>
          </div>
          <Button variant="outline-warning" size="sm" onClick={() => setShowCloseSessionModal(true)}>
            <Unlock size={14} className="me-1" />
            <span className="d-none d-sm-inline">Fermer la session</span>
            <span className="d-sm-none">Fermer</span>
          </Button>
        </Alert>
      )}

      {/* Statistiques */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-danger">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Total Dépenses</p>
                  <h3 className="mb-0 text-danger">
                    {expenseService.formatCurrency(totalExpenses)}
                  </h3>
                </div>
                <div className="bg-danger bg-opacity-10 p-3 rounded-circle">
                  <TrendingDown className="text-danger" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Nombre</p>
                  <h3 className="mb-0">{filteredExpenses.length}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded-circle">
                  <Tag className="text-primary" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Moyenne</p>
                  <h3 className="mb-0">
                    {filteredExpenses.length > 0
                      ? expenseService.formatCurrency(totalExpenses / filteredExpenses.length)
                      : '0 FCFA'}
                  </h3>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded-circle">
                  <Calendar className="text-success" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Période</p>
                  <h5 className="mb-0">
                    {filters.period === 'today' && 'Aujourd\'hui'}
                    {filters.period === 'week' && 'Cette semaine'}
                    {filters.period === 'month' && 'Ce mois'}
                    {filters.period === 'year' && 'Cette année'}
                    {filters.period === 'all' && 'Toutes'}
                    {filters.period === 'custom' && 'Personnalisée'}
                  </h5>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded-circle">
                  <Filter className="text-warning" size={24} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtres */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            {/* Période */}
            <Col md={3}>
              <Form.Label>Période</Form.Label>
              <Form.Select
                value={filters.period}
                onChange={(e) => handlePeriodChange(e.target.value)}
              >
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="year">Cette année</option>
                <option value="all">Toutes les dépenses</option>
                <option value="custom">Personnalisée</option>
              </Form.Select>
            </Col>

            {/* Dates personnalisées */}
            {filters.period === 'custom' && (
              <>
                <Col md={2}>
                  <Form.Label>Date début</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label>Date fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </Col>
              </>
            )}

            {/* Catégorie */}
            <Col md={3}>
              <Form.Label>Catégorie</Form.Label>
              <Form.Select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="all">Toutes les catégories</option>
                {expenseService.getExpenseCategories().map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Créateur (Manager uniquement) */}
            {isManager && (
              <Col md={3}>
                <Form.Label>Créé par</Form.Label>
                <Form.Select
                  value={filters.creator}
                  onChange={(e) => setFilters(prev => ({ ...prev, creator: e.target.value }))}
                >
                  <option value="all">Tous</option>
                  {getUniqueCreators().map(creator => (
                    <option key={creator} value={creator}>{creator}</option>
                  ))}
                </Form.Select>
              </Col>
            )}

            {/* Recherche */}
            <Col md={filters.period === 'custom' ? 12 : 3}>
              <Form.Label>Recherche</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Rechercher une dépense..."
                  value={filters.searchQuery}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                />
                {filters.searchQuery && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setFilters(prev => ({ ...prev, searchQuery: '' }))}
                  >
                    <X size={16} />
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Erreur */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Liste des dépenses */}
      <Card>
        <Card.Header className="bg-white">
          <h5 className="mb-0">
            Liste des dépenses ({filteredExpenses.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Chargement des dépenses...</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-5">
              <TrendingDown size={48} className="text-muted mb-3" />
              <p className="text-muted">Aucune dépense trouvée</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Date dépense</th>
                  <th>Description</th>
                  <th>Catégorie</th>
                  {isManager && <th>Créé par</th>}
                  <th className="text-end">Montant</th>
                  {isManager && ( <th className="text-center">Actions</th> )}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense) => {
                  const category = expenseService.getExpenseCategories()
                    .find(cat => cat.value === expense.category);

                  return (
                    <tr key={expense.id}>
                      <td>
                        <div className="text-nowrap">
                            {/* ✅ CORRECTION : Utiliser expense.date */}
                            {new Date(expense.date).toLocaleDateString('fr-FR')}
                            <br />
                            <small className="text-muted">
                            {new Date(expense.date).toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                            </small>
                        </div>
                        </td>
                      <td>
                        <strong>{expense.description}</strong>
                      </td>
                      <td>
                        <Badge bg="secondary">
                          {category?.icon} {category?.label}
                        </Badge>
                      </td>
                      {isManager && (
                        <td>
                          <div className="d-flex align-items-center">
                            <User size={14} className="me-1" />
                            {expense.createdByName}
                          </div>
                        </td>
                      )}
                      <td className="text-end">
                        <strong className="text-danger">
                          -{expenseService.formatCurrency(expense.amount)}
                        </strong>
                      </td>
                      <td className="text-center">
                        {(isManager || expense.createdByName === `${user.firstName} ${user.lastName}`) && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteExpense(expense.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Création Dépense */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nouvelle Dépense</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Montant *</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                  placeholder="0"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                />
                <InputGroup.Text>FCFA</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Description de la dépense"
                value={newExpense.description}
                onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Catégorie *</Form.Label>
              <Form.Select
                value={newExpense.category}
                onChange={(e) => setNewExpense(prev => ({ ...prev, category: e.target.value }))}
              >
                {expenseService.getExpenseCategories().map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.icon} {cat.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Date et heure *</Form.Label>
              <Form.Control
                type="datetime-local"
                value={newExpense.date}
                onChange={(e) => setNewExpense(prev => ({ ...prev, date: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleCreateExpense}>
            <Plus size={18} className="me-2" />
            Créer la dépense
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Export Excel */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <Download size={24} className="me-2" />
            Exporter les dépenses
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <strong>ℹ️ Information</strong>
            <p className="mb-0 mt-2">
              Sélectionnez une plage de dates pour exporter les dépenses vers Excel.
              {!isManager && ' Seules vos dépenses seront exportées.'}
            </p>
          </Alert>

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de début *</Form.Label>
                  <Form.Control
                    type="date"
                    value={exportDates.startDate}
                    onChange={(e) => setExportDates(prev => ({ ...prev, startDate: e.target.value }))}
                    max={exportDates.endDate}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date de fin *</Form.Label>
                  <Form.Control
                    type="date"
                    value={exportDates.endDate}
                    onChange={(e) => setExportDates(prev => ({ ...prev, endDate: e.target.value }))}
                    min={exportDates.startDate}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="bg-light p-3 rounded">
              <h6 className="mb-2">Aperçu :</h6>
              <p className="mb-1">
                <strong>Période :</strong> {new Date(exportDates.startDate).toLocaleDateString('fr-FR')} 
                {' → '} 
                {new Date(exportDates.endDate).toLocaleDateString('fr-FR')}
              </p>
              <p className="mb-0">
                <strong>Boutique :</strong> {currentBoutique?.name || 'N/A'}
              </p>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowExportModal(false)}
            disabled={exporting}
          >
            Annuler
          </Button>
          <Button 
            variant="success" 
            onClick={handleExportToExcel}
            disabled={exporting || !exportDates.startDate || !exportDates.endDate}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Export en cours...
              </>
            ) : (
              <>
                <Download size={18} className="me-2" />
                Exporter vers Excel
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ✅ Modal de fermeture de session */}
      {currentSession && (
        <CloseSessionModal
          show={showCloseSessionModal}
          session={currentSession}
          onClose={() => setShowCloseSessionModal(false)}
          onSessionClosed={handleSessionClosed}
        />
      )}
    </Container>
  );
};

export default ExpensesList;